import { Head, Link, router } from "@inertiajs/react"
import {
  ArrowDownCircle,
  ArrowLeftRight,
  ArrowUpCircle,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  Landmark,
  Pencil,
  PlusCircle,
  Receipt,
  Trash2,
} from "lucide-react"
import { useCallback, useRef, useState } from "react"

import { CategorySelect } from "@/components/category-select"
import Heading from "@/components/heading"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import AppLayout from "@/layouts/app-layout"
import { formatCurrency, formatDate } from "@/lib/utils"
import {
  bulkUpdateEntriesPath,
  categorizationRulesPath,
  entriesPath,
  entryPath,
  newEntryPath,
  previewCategorizationRulesPath,
  scheduleCPath,
} from "@/routes"
import type { BreadcrumbItem, Category, Entry } from "@/types"

interface Pagination {
  page: number
  total_pages: number
  total_count: number
}

interface Filters {
  start_date: string
  end_date: string
  q: string
  category_id: string
  status: string
  direction: string
  schedule_c_year: string
}

interface Totals {
  expenses: number
  income: number
  net_income: number
  taxes: number
  transfers: number
}

interface EntriesProps {
  entries: Entry[]
  categories: Category[]
  totals: Totals
  pagination: Pagination
  filters: Filters
}

interface MatchingEntry {
  id: number
  description: string
  date: string
  amount_cents: number
  direction: string
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Entries", href: entriesPath() },
]

const DATE_PRESETS = [
  { label: "All", start: "", end: "" },
  { label: "This Month", start: firstOfMonth(0), end: endOfMonth(0) },
  { label: "Last Month", start: firstOfMonth(-1), end: endOfMonth(-1) },
  { label: "This Year", start: firstOfYear(0), end: endOfYear(0) },
  { label: "Last Year", start: firstOfYear(-1), end: endOfYear(-1) },
]

function firstOfMonth(offset: number) {
  const d = new Date()
  d.setMonth(d.getMonth() + offset, 1)
  return d.toISOString().split("T")[0]
}

function endOfMonth(offset: number) {
  const d = new Date()
  d.setMonth(d.getMonth() + offset + 1, 0)
  return d.toISOString().split("T")[0]
}

function firstOfYear(offset: number) {
  return `${new Date().getFullYear() + offset}-01-01`
}

function endOfYear(offset: number) {
  return `${new Date().getFullYear() + offset}-12-31`
}

function isMatchingEntries(value: unknown): value is MatchingEntry[] {
  return Array.isArray(value) && value.every(isMatchingEntry)
}

function isMatchingEntry(value: unknown): value is MatchingEntry {
  if (typeof value !== "object" || value === null) {
    return false
  }

  const entry = value as Record<string, unknown>

  return (
    typeof entry.id === "number" &&
    typeof entry.description === "string" &&
    typeof entry.date === "string" &&
    typeof entry.amount_cents === "number" &&
    typeof entry.direction === "string"
  )
}

export default function Entries({
  entries,
  categories,
  totals,
  pagination,
  filters,
}: EntriesProps) {
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false)
  const [rulePattern, setRulePattern] = useState("")
  const [ruleCategoryId, setRuleCategoryId] = useState<number | null>(null)
  const [ruleTagNames, setRuleTagNames] = useState("")
  const [applyToExisting, setApplyToExisting] = useState(true)
  const [matchingEntries, setMatchingEntries] = useState<MatchingEntry[]>([])
  const [showCustomDate, setShowCustomDate] = useState(false)
  const [customStart, setCustomStart] = useState("")
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [bulkCategoryId, setBulkCategoryId] = useState("")
  const [bulkTagNames, setBulkTagNames] = useState("")

  function toggleSelected(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleAll() {
    if (selectedIds.size === entries.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(entries.map((e) => e.id)))
    }
  }

  function handleBulkApply() {
    if (selectedIds.size === 0 || !bulkCategoryId) return
    router.patch(
      bulkUpdateEntriesPath(),
      {
        entry_ids: Array.from(selectedIds),
        category_id: bulkCategoryId,
      },
      {
        preserveScroll: true,
        onFinish: () => {
          setSelectedIds(new Set())
          setBulkCategoryId("")
          setBulkTagNames("")
          router.reload()
        },
      },
    )
  }
  const [customEnd, setCustomEnd] = useState("")

  function activeFilters(): Partial<Record<string, string>> {
    const params: Partial<Record<string, string>> = {}
    if (filters.start_date) params.start_date = filters.start_date
    if (filters.end_date) params.end_date = filters.end_date
    if (filters.q) params.q = filters.q
    if (filters.category_id) params.category_id = filters.category_id
    if (filters.status) params.status = filters.status
    if (filters.direction) params.direction = filters.direction
    if (filters.schedule_c_year)
      params.schedule_c_year = filters.schedule_c_year
    return params
  }

  function categoryFilterValue(): string {
    if (filters.status === "needs_review") return "__uncategorized__"
    if (filters.category_id) return filters.category_id
    return "__all__"
  }

  function applyDateFilter(start: string, end: string) {
    setShowCustomDate(false)
    const query = { ...activeFilters() }
    delete query.start_date
    delete query.end_date

    if (start && end) {
      query.start_date = start
      query.end_date = end
    }
    router.get(entriesPath(), query, { preserveState: true })
  }

  function applyCustomDate() {
    if (customStart && customEnd) {
      applyDateFilter(customStart, customEnd)
    }
  }

  function isActivePreset(start: string, end: string) {
    if (!start && !end) return !filters.start_date && !filters.end_date
    return filters.start_date === start && filters.end_date === end
  }

  function handleCategoryChange(entry: Entry, categoryId: string) {
    const catId = categoryId === "uncategorized" ? null : Number(categoryId)

    // Capture dialog data before the request
    const shouldPrompt = catId != null
    const pattern = entry.description.toLowerCase().trim()

    router.patch(
      entryPath({ id: entry.id }),
      { category_id: catId, tag_names: entry.tags },
      {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => {
          if (shouldPrompt) {
            setRulePattern(pattern)
            setRuleCategoryId(catId)
            setRuleTagNames(entry.tags.join(", "))
            fetchMatchingEntries(pattern)
            setRuleDialogOpen(true)
          }
        },
      },
    )
  }

  function handleCreateRule() {
    if (!rulePattern || !ruleCategoryId) return
    router.post(
      categorizationRulesPath(),
      {
        match_pattern: rulePattern,
        category_id: ruleCategoryId,
        tag_names: ruleTagNames,
        apply_to_existing: applyToExisting ? "true" : "false",
      },
      {
        preserveScroll: true,
        preserveState: true,
        onFinish: () => {
          setRuleDialogOpen(false)
          setApplyToExisting(true)
        },
      },
    )
  }

  function handleDelete(entry: Entry) {
    if (confirm(`Delete "${entry.description}"?`)) {
      router.delete(entryPath({ id: entry.id }), { preserveScroll: true })
    }
  }

  const fetchTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  )

  const fetchMatchingEntries = useCallback((pattern: string) => {
    clearTimeout(fetchTimerRef.current)
    if (!pattern.trim()) {
      setMatchingEntries([])
      return
    }
    fetchTimerRef.current = setTimeout(async () => {
      try {
        const url = `${previewCategorizationRulesPath()}?pattern=${encodeURIComponent(pattern)}`
        const res = await fetch(url, {
          headers: { Accept: "application/json" },
        })
        if (res.ok) {
          const entries: unknown = await res.json()
          setMatchingEntries(isMatchingEntries(entries) ? entries : [])
        }
      } catch {
        setMatchingEntries([])
      }
    }, 300)
  }, [])

  function handleRuleDialogOpenChange(open: boolean) {
    setRuleDialogOpen(open)
    if (!open) {
      clearTimeout(fetchTimerRef.current)
      setMatchingEntries([])
    }
  }

  const selectedCategory = categories.find((c) => c.id === ruleCategoryId)
  const summaryTotals = [
    {
      label: "Expenses",
      amount: totals.expenses,
      icon: ArrowDownCircle,
      valueClassName: "text-red-600",
      iconClassName: "text-red-500",
    },
    {
      label: "Income",
      amount: totals.income,
      icon: ArrowUpCircle,
      valueClassName: "text-green-600",
      iconClassName: "text-green-500",
    },
    {
      label: "Net Income",
      amount: totals.net_income,
      icon: DollarSign,
      valueClassName: "text-foreground",
      iconClassName: "text-muted-foreground",
    },
    {
      label: "Taxes",
      amount: totals.taxes,
      icon: Landmark,
      valueClassName: "text-amber-600",
      iconClassName: "text-amber-500",
    },
    {
      label: "Transfers",
      amount: totals.transfers,
      icon: ArrowLeftRight,
      valueClassName: "text-foreground",
      iconClassName: "text-muted-foreground",
    },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Entries" />

      <div className="mx-auto w-full max-w-4xl space-y-6 p-4">
        {filters.schedule_c_year && (
          <div>
            <Button variant="outline" size="sm" asChild>
              <Link href={scheduleCPath({ year: filters.schedule_c_year })}>
                Return to Schedule C worksheet
              </Link>
            </Button>
          </div>
        )}
        <div className="flex items-center justify-between">
          <Heading
            title="Entries"
            description="Track your income and expenses"
          />
          <Button asChild size="sm">
            <Link href={newEntryPath()}>
              <PlusCircle className="mr-2 h-4 w-4" />
              Add Entry
            </Link>
          </Button>
        </div>

        <div className="space-y-3">
          <div className="flex gap-2">
            <Input
              type="search"
              placeholder="Search entries..."
              defaultValue={filters.q}
              className="flex-1"
              onChange={(e) => {
                const value = e.target.value
                clearTimeout(
                  (
                    window as unknown as Record<
                      string,
                      ReturnType<typeof setTimeout>
                    >
                  ).__searchTimer,
                )
                ;(
                  window as unknown as Record<
                    string,
                    ReturnType<typeof setTimeout>
                  >
                ).__searchTimer = setTimeout(() => {
                  const query = { ...activeFilters(), q: value }
                  if (!value) Reflect.deleteProperty(query, "q")
                  router.get(entriesPath(), query, { preserveState: true })
                }, 300)
              }}
            />
            <Select
              value={categoryFilterValue()}
              onValueChange={(value) => {
                const query = { ...activeFilters() }
                delete query.category_id
                delete query.status
                if (value === "__uncategorized__") {
                  query.status = "needs_review"
                } else if (value !== "__all__") {
                  query.category_id = value
                }
                router.get(entriesPath(), query, { preserveState: true })
              }}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Categories</SelectItem>
                <SelectItem value="__uncategorized__">Uncategorized</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={String(cat.id)}>
                    <div className="flex items-center gap-2">
                      <div
                        className="h-2 w-2 rounded-full"
                        style={{ backgroundColor: cat.color }}
                      />
                      {cat.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {(["", "expense", "revenue", "transfer", "tax"] as const).map(
              (dir) => {
                const label =
                  dir === ""
                    ? "All"
                    : dir === "expense"
                      ? "Expenses"
                      : dir === "revenue"
                        ? "Income"
                        : dir === "transfer"
                          ? "Transfers"
                          : "Tax"
                return (
                  <Button
                    key={dir}
                    variant={filters.direction === dir ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      const query = { ...activeFilters() }
                      if (dir) {
                        query.direction = dir
                      } else {
                        delete query.direction
                      }
                      router.get(entriesPath(), query, { preserveState: true })
                    }}
                  >
                    {label}
                  </Button>
                )
              },
            )}
            <div className="bg-border mx-1 h-5 w-px" />
            {DATE_PRESETS.map((preset) => (
              <Button
                key={preset.label}
                variant={
                  isActivePreset(preset.start, preset.end)
                    ? "default"
                    : "outline"
                }
                size="sm"
                onClick={() => applyDateFilter(preset.start, preset.end)}
              >
                {preset.label}
              </Button>
            ))}
            <Button
              variant={showCustomDate ? "default" : "outline"}
              size="sm"
              onClick={() => setShowCustomDate(!showCustomDate)}
            >
              Custom
            </Button>
          </div>

          {showCustomDate && (
            <div className="flex items-end gap-3">
              <div className="grid gap-1">
                <Label className="text-xs">From</Label>
                <Input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                />
              </div>
              <div className="grid gap-1">
                <Label className="text-xs">To</Label>
                <Input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                />
              </div>
              <Button size="sm" onClick={applyCustomDate}>
                Apply
              </Button>
            </div>
          )}
        </div>

        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
          {summaryTotals.map((total) => {
            const Icon = total.icon

            return (
              <div
                key={total.label}
                className="rounded-lg border px-4 py-3"
                aria-label={`${total.label} total ${formatCurrency(total.amount)}`}
              >
                <div className="text-muted-foreground flex items-center gap-2 text-xs font-medium">
                  <Icon className={`h-4 w-4 ${total.iconClassName}`} />
                  {total.label}
                </div>
                <p
                  className={`mt-2 font-mono text-lg font-semibold ${total.valueClassName}`}
                >
                  {formatCurrency(total.amount)}
                </p>
              </div>
            )
          })}
        </div>

        {pagination.total_count > 0 && (
          <div className="flex items-center justify-between">
            <p className="text-muted-foreground text-sm">
              Showing {entries.length} of {pagination.total_count} entries
            </p>
            {entries.length > 0 && (
              <label className="text-muted-foreground flex cursor-pointer items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={
                    selectedIds.size === entries.length && entries.length > 0
                  }
                  onChange={toggleAll}
                  className="border-input h-4 w-4 rounded"
                />
                Select all
              </label>
            )}
          </div>
        )}

        {selectedIds.size > 0 && (
          <div className="bg-muted/50 flex items-center gap-3 rounded-lg border px-4 py-3">
            <span className="text-sm font-medium">
              {selectedIds.size} selected
            </span>
            <Select
              value={bulkCategoryId === "" ? "__pick__" : bulkCategoryId}
              onValueChange={(v) =>
                setBulkCategoryId(v === "__pick__" ? "" : v)
              }
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__pick__">Select category</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={String(cat.id)}>
                    <div className="flex items-center gap-2">
                      <div
                        className="h-2 w-2 rounded-full"
                        style={{ backgroundColor: cat.color }}
                      />
                      {cat.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              disabled={!bulkCategoryId}
              onClick={handleBulkApply}
            >
              Apply
            </Button>
            <Input
              value={bulkTagNames}
              onChange={(e) => setBulkTagNames(e.target.value)}
              placeholder="Add tags"
              className="w-[150px]"
            />
            <Button
              size="sm"
              disabled={!bulkTagNames.trim()}
              onClick={() => {
                router.patch(
                  bulkUpdateEntriesPath(),
                  {
                    entry_ids: Array.from(selectedIds),
                    tag_names: bulkTagNames,
                  },
                  {
                    preserveScroll: true,
                    onFinish: () => {
                      setSelectedIds(new Set())
                      setBulkTagNames("")
                      router.reload()
                    },
                  },
                )
              }}
            >
              Add tags
            </Button>
            <div className="bg-border mx-1 h-5 w-px" />
            {["transfer", "tax"].map((dir) => (
              <Button
                key={dir}
                size="sm"
                variant="outline"
                onClick={() => {
                  router.patch(
                    bulkUpdateEntriesPath(),
                    { entry_ids: Array.from(selectedIds), direction: dir },
                    {
                      preserveScroll: true,
                      preserveState: true,
                      onFinish: () => {
                        setSelectedIds(new Set())
                        router.reload()
                      },
                    },
                  )
                }}
              >
                {dir === "transfer" ? (
                  <>
                    <ArrowLeftRight className="mr-1 h-3 w-3" /> Transfer
                  </>
                ) : (
                  <>
                    <Landmark className="mr-1 h-3 w-3" /> Tax
                  </>
                )}
              </Button>
            ))}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setSelectedIds(new Set())
                setBulkCategoryId("")
                setBulkTagNames("")
              }}
            >
              Cancel
            </Button>
          </div>
        )}

        {entries.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-12 text-center">
            <Receipt className="text-muted-foreground mb-4 h-12 w-12" />
            <h3 className="mb-2 text-lg font-medium">No entries yet</h3>
            <p className="text-muted-foreground mb-6 max-w-sm text-sm">
              Start tracking! Add your first entry or import a CSV.
            </p>
            <Button asChild>
              <Link href={newEntryPath()}>
                <PlusCircle className="mr-2 h-4 w-4" />
                Add Entry
              </Link>
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {entries.map((entry) => (
              <div
                key={entry.id}
                className="flex items-center justify-between rounded-lg border px-4 py-3"
              >
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(entry.id)}
                    onChange={() => toggleSelected(entry.id)}
                    className="border-input h-4 w-4 rounded"
                  />
                  {entry.direction === "revenue" ? (
                    <ArrowUpCircle className="h-4 w-4 text-green-500" />
                  ) : entry.direction === "transfer" ? (
                    <ArrowLeftRight className="text-muted-foreground h-4 w-4" />
                  ) : entry.direction === "tax" ? (
                    <Landmark className="h-4 w-4 text-amber-500" />
                  ) : (
                    <ArrowDownCircle className="h-4 w-4 text-red-500" />
                  )}
                  <div className="flex min-w-0 flex-1 items-center">
                    <span
                      className="min-w-0 flex-1 truncate font-medium"
                      title={entry.description}
                    >
                      {entry.description}
                    </span>
                    <span className="text-muted-foreground mr-3 ml-2 shrink-0 text-sm whitespace-nowrap">
                      {formatDate(entry.date)}
                    </span>
                  </div>
                </div>

                <div className="flex shrink-0 items-center gap-3">
                  <CategorySelect
                    value={
                      entry.category
                        ? String(entry.category.id)
                        : "uncategorized"
                    }
                    onValueChange={(value) =>
                      handleCategoryChange(entry, value)
                    }
                    categories={categories}
                    allowUncategorized
                    triggerClassName="w-[160px]"
                  />
                  {entry.tags.length > 0 && (
                    <span className="text-muted-foreground max-w-[180px] truncate text-sm">
                      {entry.tags.map((tag) => `#${tag}`).join(", ")}
                    </span>
                  )}

                  <span
                    className={`w-24 text-right font-mono text-sm font-medium ${
                      entry.direction === "revenue"
                        ? "text-green-600"
                        : entry.direction === "transfer" ||
                            entry.direction === "tax"
                          ? "text-muted-foreground"
                          : "text-red-600"
                    }`}
                  >
                    {entry.direction === "revenue"
                      ? "+"
                      : entry.direction === "transfer" ||
                          entry.direction === "tax"
                        ? ""
                        : "-"}
                    {formatCurrency(entry.amount)}
                  </span>

                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" asChild>
                      <Link href={`/entries/${entry.id}/edit`}>
                        <Pencil className="h-4 w-4" />
                      </Link>
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(entry)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-muted-foreground text-sm">
              {pagination.total_count} entries
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page <= 1}
                onClick={() =>
                  router.get(
                    entriesPath(),
                    { ...activeFilters(), page: pagination.page - 1 },
                    { preserveState: true },
                  )
                }
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm">
                {pagination.page} / {pagination.total_pages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page >= pagination.total_pages}
                onClick={() =>
                  router.get(
                    entriesPath(),
                    { ...activeFilters(), page: pagination.page + 1 },
                    { preserveState: true },
                  )
                }
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <Dialog open={ruleDialogOpen} onOpenChange={handleRuleDialogOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remember this?</DialogTitle>
            <DialogDescription>
              Auto-categorize future entries matching this pattern.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="rule-pattern">Match pattern</Label>
              <Input
                id="rule-pattern"
                value={rulePattern}
                onChange={(e) => {
                  const value = e.target.value
                  setRulePattern(value)
                  fetchMatchingEntries(value)
                }}
              />
              <p className="text-muted-foreground text-xs">
                Future entries containing &ldquo;{rulePattern}&rdquo; will be
                categorized as{" "}
                <strong>{selectedCategory?.name ?? "..."}</strong>.
              </p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="rule-tag-names">Tags (optional)</Label>
              <Input
                id="rule-tag-names"
                value={ruleTagNames}
                onChange={(e) => setRuleTagNames(e.target.value)}
                placeholder="e.g. Tanooki, Payroll"
              />
              <p className="text-muted-foreground text-xs">
                Separate multiple tags with commas. Matching entries will
                receive these tags.
              </p>
            </div>

            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={applyToExisting}
                onChange={(e) => setApplyToExisting(e.target.checked)}
                className="border-input h-4 w-4 rounded"
              />
              <span className="text-sm">
                Also apply to{" "}
                {matchingEntries.length > 0 ? matchingEntries.length : ""}{" "}
                existing uncategorized{" "}
                {matchingEntries.length === 1 ? "entry" : "entries"}
              </span>
            </label>

            {applyToExisting && matchingEntries.length > 0 && (
              <div className="max-h-48 overflow-y-auto rounded-md border">
                {matchingEntries.map((entry) => (
                  <div
                    key={entry.id}
                    className="flex items-center justify-between border-b px-3 py-2 last:border-b-0"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">
                        {entry.description}
                      </p>
                      <p className="text-muted-foreground text-xs">
                        {formatDate(entry.date)}
                      </p>
                    </div>
                    <span
                      className={`ml-3 shrink-0 font-mono text-sm ${
                        entry.direction === "revenue"
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {entry.direction === "revenue" ? "+" : "-"}
                      {formatCurrency(entry.amount_cents / 100)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setRuleDialogOpen(false)}
            >
              Skip
            </Button>
            <Button type="button" onClick={handleCreateRule}>
              Remember
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  )
}
